import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal } from "../../components/erp";
import { errorText } from "../../api/client";
import { ppDropdownsApi, valueAdditionApi, ValueAdditionRow, VaGradingBreakupRow } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const VaModal: React.FC<{ trackId: string; plantId: string; breakups: VaGradingBreakupRow[]; editRow?: ValueAdditionRow;
  onClose: () => void; onSaved: () => void }> = ({ trackId, plantId, breakups, editRow, onClose, onSaved }) => {
  const dd = useQuery({ queryKey: ["pp", "dropdowns"], queryFn: ppDropdownsApi.get, staleTime: 5 * 60_000 });
  const shifts = dd.data?.Data?.Shift || []; const supervisors = dd.data?.Data?.Supervisor || []; const centers = dd.data?.Data?.Center || [];
  const [v, setV] = useState({
    GradingBreakupID: String(editRow?.GradingBreakupID ?? ""), StationID: "", ShiftID: "", Supervisor: "",
    TransactionDate: today(), HLCount: String(editRow?.HLCount ?? ""), HLQuantity: "", Crates: String(editRow?.NoofCrates ?? "1"),
    ReceivedWeight: "", ReturnWeight: "", WeightAfterVA: String(editRow?.WtAftrVA ?? ""), CountAfterVA: String(editRow?.CountAftrVA ?? ""),
    CountNetYield: "", IceConsumption: "", Remarks: editRow?.Remarks ?? "",
  });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const chosen = breakups.find((b) => String(b.GradingBreakUpID) === v.GradingBreakupID);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await valueAdditionApi.post({
        BreakUpID: editRow?.BreakUpID ?? "", ArrivalID: chosen?.ArrivalID ?? editRow?.ArrivalID, VarietyID: chosen?.VarietyID ?? editRow?.VarietyID,
        PlantID: plantId, TrackID: trackId, ReceivalBreakupID: chosen?.ReceivalBreakupID, ...v,
      });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={editRow ? "Edit Value Addition" : "New Value Addition"} onClose={onClose} testId="va-modal">
      <form onSubmit={submit} data-testid="va-form">
        {!editRow && (
          <div className="mb-3"><label className="form-label">Grading Breakup (balance)</label>
            <ErpSelect id="va-breakup" value={v.GradingBreakupID} onChange={(val) => setV((s) => ({ ...s, GradingBreakupID: val }))}
              options={breakups.map((b) => ({ value: String(b.GradingBreakUpID), label: `${b.Variety || b.VarietyID} — HL${b.HLCount} — Bal ${b.BalanceQty}kg / ${b.BalanceCrates} crates` }))}
              testId="va-breakup" /></div>
        )}
        <div className="row">
          <div className="col-md-4 mb-3"><label className="form-label">Center</label>
            <select className="form-select" value={v.StationID} onChange={(e) => setV((s) => ({ ...s, StationID: e.target.value }))} data-testid="va-center">
              <option value="">Select</option>{centers.map((c) => <option key={c.StationID} value={c.StationID}>{c.UnitName}</option>)}
            </select></div>
          <div className="col-md-4 mb-3"><label className="form-label">Shift</label>
            <select className="form-select" value={v.ShiftID} onChange={(e) => setV((s) => ({ ...s, ShiftID: e.target.value }))} data-testid="va-shift">
              <option value="">Select</option>{shifts.map((o) => <option key={o.OptID} value={o.OptID}>{o.Name}</option>)}
            </select></div>
          <div className="col-md-4 mb-3"><label className="form-label">Supervisor</label>
            <select className="form-select" value={v.Supervisor} onChange={(e) => setV((s) => ({ ...s, Supervisor: e.target.value }))} data-testid="va-supervisor">
              <option value="">Select</option>{supervisors.map((o) => <option key={o.ID} value={o.ID}>{o.UserName}</option>)}
            </select></div>
        </div>
        <div className="row">
          {(["HLCount", "HLQuantity", "Crates", "ReceivedWeight", "ReturnWeight", "WeightAfterVA", "CountAfterVA"] as const).map((k) => (
            <div className="col-md-3 mb-3" key={k}><label className="form-label">{k}</label>
              <input className="form-control" value={(v as any)[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} data-testid={`va-${k.toLowerCase()}`} /></div>
          ))}
        </div>
        <div className="mb-3"><label className="form-label">Remarks</label>
          <input className="form-control" value={v.Remarks} onChange={(e) => setV((s) => ({ ...s, Remarks: e.target.value }))} data-testid="va-remarks" /></div>
        {err && <Alert kind="danger" testId="va-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="va-submit">{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/PreProcessing/value-addition-trackid.php — consumes count-wise PpcHLGradingBreakup output into
 * PpcValueAddition entries; enforces HL-weight-exceeded and crates-exceeded guards before saving. */
export default function ValueAdditionPage() {
  const [params] = useSearchParams();
  const trackId = params.get("TrackID") || "";
  const plantId = params.get("PlantID") || "";
  const qc = useQueryClient();

  const detail = useQuery({ queryKey: ["pp", "va", trackId, plantId], enabled: !!(trackId && plantId),
    queryFn: () => valueAdditionApi.get({ TrackID: trackId, PlantID: plantId }) });

  const rows = detail.data?.ValueAdditionDetails?.ValueAdditionData || [];
  const totals = detail.data?.ValueAdditionDetails?.Totals;
  const breakups = detail.data?.GradingBreakUpDetails?.GradingData || [];
  const [showNew, setShowNew] = useState(false);
  const [editRow, setEditRow] = useState<ValueAdditionRow | null>(null);

  const del = async (r: ValueAdditionRow) => {
    if (!window.confirm("Delete this Value Addition entry?")) return;
    await valueAdditionApi.delete({ BreakUpID: r.BreakUpID, TrackID: trackId, PlantID: plantId });
    qc.invalidateQueries({ queryKey: ["pp", "va", trackId, plantId] });
  };

  return (
    <>
      <PageHeader title="Value Addition" location="Preprocessing" />
      <div className="card"><div className="card-body">
        <div className="d-flex justify-content-between mb-3"><h6>Value Addition Entries</h6>
          <button className="btn btn-sm btn-success" data-testid="va-add-new" onClick={() => setShowNew(true)}>+ New Value Addition</button></div>
        <ErpTable<ValueAdditionRow> testId="va-table" loading={detail.isLoading} error={detail.isError ? errorText(detail.error) : null}
          rows={rows} columns={[{ key: "LotNo", title: "Lot No" }, { key: "Variety", title: "Variety" }, { key: "HLCount", title: "HL Count" },
            { key: "HLQuantityKgs", title: "HL Qty" }, { key: "NoofCrates", title: "Crates" }, { key: "WtAftrVA", title: "Wt After VA" },
            { key: "CountAftrVA", title: "Count After VA" }, { key: "SoakingPlant", title: "Soaking Plant" },
            { key: "Action", title: "Action", render: (r) => (
              <div className="d-flex gap-1">
                <button className="btn btn-sm btn-outline-primary" data-testid={`va-edit-${r.BreakUpID}`} onClick={() => setEditRow(r)}>Edit</button>
                <button className="btn btn-sm btn-outline-danger" data-testid={`va-delete-${r.BreakUpID}`} onClick={() => del(r)}>Delete</button>
              </div>
            ) }]}
          footer={totals && <tr><td colSpan={3}><strong>Total</strong></td><td>{totals.TotalHLQuantity}</td><td colSpan={4} /></tr>} />
      </div></div>
      {(showNew || editRow) && <VaModal trackId={trackId} plantId={plantId} breakups={breakups} editRow={editRow || undefined}
        onClose={() => { setShowNew(false); setEditRow(null); }}
        onSaved={() => { setShowNew(false); setEditRow(null); qc.invalidateQueries({ queryKey: ["pp", "va", trackId, plantId] }); }} />}
    </>
  );
}
